/**
 * 학원(academy) 서비스 — 멀티테넌트 핵심
 *
 * - 학원 생성/조회
 * - 사용자가 속한 학원 목록 (users/{uid}.memberships 기반)
 * - 학부모 가입 신청 / 원장 승인
 */
import {
  getDoc, getDocs, setDoc, updateDoc, deleteDoc, addDoc, arrayUnion, arrayRemove,
  increment, serverTimestamp, query, where, orderBy, onSnapshot,
} from 'firebase/firestore';
import {
  academiesCol, academyDoc, userDoc, memberDoc, membersCol,
  joinRequestsCol, joinRequestDoc,
  studentsCol, studentDoc, attendanceLogCol, attendanceLogDoc,
  chatRoomDoc, messagesCol,
  classesCol, classDoc, attendanceCol, attendanceDoc, noticesCol, noticeDoc,
  assignmentsCol, assignmentDoc,
  logsCol, logDoc,
  materialsCol, materialDoc,
  gradesCol, gradeDoc,
  academyEventsCol, academyEventDoc,
  academyNoticesCol, academyNoticeDoc,
  academyMaterialsCol, academyMaterialDoc,
} from '../firebase/academyPaths';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase/config';
import { compressImage } from '../utils/imageUtils';
import { toE164KR } from './recoveryService';

const academyService = {
  /**
   * 학원 생성 — 등록자가 원장(owner)이 된다.
   * status: 'pending' → 1등급 운영자 승인 후 'approved' (디렉토리 노출)
   */
  createAcademy: async (ownerId, data) => {
    const id = `academy-${Date.now()}`;
    const academy = {
      name: data.name || '',
      field: data.field || 'study',     // study | sports | arts
      subject: data.subject || (data.subjects && data.subjects[0]) || '',
      subjects: data.subjects || [],
      type: data.type || 'academy',     // home(공부방) | academy(학원)
      photoUrl: data.photoUrl || '',    // 필수 — 없으면 운영자 승인 안 함
      location: data.location || '',
      phone: data.phone || '',
      target: data.target || '',
      grades: data.grades || [],
      hours: data.hours || [],
      shuttle: !!data.shuttle,
      tuition: data.tuition || [],
      hashtags: data.hashtags || [],
      regNo: data.regNo || '',
      homepage: data.homepage || '',
      instagram: data.instagram || '',
      blog: data.blog || '',
      intro: data.intro || '',
      posts: [],
      recommend: 0,
      likes: 0,
      features: { attendance: true, notice: true, material: true, assignment: true, grade: true },
      ownerId,
      status: 'approved',   // 자동 승인 — 등록 즉시 디렉토리 노출
      approvedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
    };
    await setDoc(academyDoc(id), academy);
    // 원장을 구성원으로 등록
    await setDoc(memberDoc(id, ownerId), {
      role: 'owner', status: 'approved', joinedAt: serverTimestamp(),
    });
    // 사용자 memberships에 추가
    await setDoc(userDoc(ownerId), {
      memberships: arrayUnion({ academyId: id, role: 'owner' }),
    }, { merge: true });
    return { id, ...academy };
  },

  /**
   * 내 draft 학원 조회 — ownerId가 본인이고 status='draft'인 학원 (1개)
   * 신청 페이지 진입 시 이어서 작성하기 위함
   */
  getMyDraftAcademy: async (uid) => {
    const q = query(
      academiesCol(),
      where('ownerId', '==', uid),
      where('status', '==', 'draft'),
    );
    const snap = await getDocs(q);
    if (snap.empty) return null;
    const d = snap.docs[0];
    return { id: d.id, ...d.data() };
  },

  /**
   * draft 학원 생성 — 사용자가 학원 신청 페이지에 처음 진입할 때
   * status='draft', memberships에는 아직 등록 안 함 (승인 요청 시점에 등록)
   */
  createDraftAcademy: async (ownerId) => {
    const id = `academy-${Date.now()}`;
    const academy = {
      name: '', field: '', subject: '', subjects: [],
      type: '', photoUrl: '', location: '', phone: '',
      grades: [], hours: [], shuttle: false,
      tuition: [], hashtags: [], regNo: '',
      homepage: '', instagram: '', blog: '', intro: '',
      posts: [], recommend: 0, likes: 0,
      features: { attendance: true, notice: true, material: true, assignment: true, grade: true },
      ownerId,
      status: 'draft',
      createdAt: serverTimestamp(),
    };
    await setDoc(academyDoc(id), academy);
    return { id, ...academy };
  },

  /** draft 항목 업데이트 (자동 임시저장) */
  updateDraftAcademy: async (aid, patch) => {
    await updateDoc(academyDoc(aid), patch);
  },

  /** draft 학원 삭제 — 승인 요청 없이 빠져나간 미완료 draft 흔적 제거 */
  deleteDraftAcademy: async (aid) => {
    await deleteDoc(academyDoc(aid));
  },

  /**
   * 학원 사진 업로드 → photoUrl 반환
   * draft 작성 중에도 사용 가능
   */
  uploadAcademyPhoto: async (aid, file) => {
    const compressed = await compressImage(file, 400, 0.8);
    const path = `academy-photos/${aid}-${Date.now()}.webp`;
    const snap = await uploadBytes(storageRef(storage, path), compressed);
    return getDownloadURL(snap.ref);
  },

  /**
   * 홍보 사진(갤러리) 업로드 → URL 반환
   * promoPhotos 배열에 { url, label } 형태로 저장됨
   */
  uploadAcademyPromoPhoto: async (aid, file) => {
    const compressed = await compressImage(file, 1200, 0.85);
    const path = `academy-promo-photos/${aid}-${Date.now()}.webp`;
    const snap = await uploadBytes(storageRef(storage, path), compressed);
    return getDownloadURL(snap.ref);
  },

  /**
   * 승인 요청 — draft → approved (자동 승인, 운영자 수동승인 없이 즉시 디렉토리 노출).
   * UI상 '승인 요청' 개념은 유지하되 실제 처리는 자동.
   * 동시에 memberships에 owner로 등록
   */
  submitDraftAcademy: async (aid, ownerId) => {
    await updateDoc(academyDoc(aid), {
      status: 'approved',
      submittedAt: serverTimestamp(),
      approvedAt: serverTimestamp(),
    });
    await setDoc(memberDoc(aid, ownerId), {
      role: 'owner', status: 'approved', joinedAt: serverTimestamp(),
    });
    await setDoc(userDoc(ownerId), {
      memberships: arrayUnion({ academyId: aid, role: 'owner' }),
    }, { merge: true });
  },

  /** 학원 단건 조회 */
  getAcademy: async (aid) => {
    const snap = await getDoc(academyDoc(aid));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  },

  /** 학원 정보 수정 */
  updateAcademy: async (aid, data) => {
    await updateDoc(academyDoc(aid), data);
  },

  /**
   * 사용자가 속한 학원 목록 (myRole 포함)
   * users/{uid}.memberships = [{ academyId, role }]
   */
  getUserAcademies: async (uid) => {
    const uSnap = await getDoc(userDoc(uid));
    const memberships = uSnap.exists() ? (uSnap.data().memberships || []) : [];
    const result = [];
    for (const m of memberships) {
      const academy = await academyService.getAcademy(m.academyId);
      if (academy) result.push({
        ...academy, myRole: m.role,
        myChildren: m.children || [], myJoinedAt: m.joinedAt || null,
      });
    }
    return result;
  },

  /** 전체 학원 목록 (운영자 admin용 — 상태 무관) */
  getAllAcademies: async () => {
    const snap = await getDocs(academiesCol());
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  },

  /**
   * 운영자 직접 등록 — 빈 학원 문서 생성(원장 미연결).
   * 이후 폼으로 채우고 publishDirectoryAcademy 로 디렉토리 노출.
   */
  createBlankAcademy: async () => {
    // 기존 미작성 빈 draft 재사용 (연타·이탈로 빈 문서 쌓이는 것 방지)
    const blanks = await getDocs(query(academiesCol(), where('ownerId', '==', ''), where('status', '==', 'draft')));
    const reuse = blanks.docs.find((d) => !((d.data().name || '').trim()));
    if (reuse) return reuse.id;
    const id = `academy-${Date.now()}`;
    await setDoc(academyDoc(id), {
      name: '', field: '', subject: '', subjects: [], type: '',
      photoUrl: '', location: '', phone: '', grades: [], hours: [], shuttle: false,
      tuition: [], hashtags: [], regNo: '', homepage: '', instagram: '', blog: '',
      intro: '', videos: [], promoPhotos: [], posts: [], recommend: 0, likes: 0,
      features: { attendance: true, notice: true, material: true, assignment: true, grade: true },
      ownerId: '',          // 미연결 (운영자가 미리 등록)
      unclaimed: true,
      status: 'draft',
      createdAt: serverTimestamp(),
    });
    return id;
  },

  /** 디렉토리 노출 — 정보 저장 + status='approved' */
  publishDirectoryAcademy: async (aid, data) => {
    await updateDoc(academyDoc(aid), { ...data, status: 'approved' });
  },

  /** 학원 삭제 (운영자용) */
  deleteAcademy: async (aid) => {
    await deleteDoc(academyDoc(aid));
  },

  /**
   * 원장 연결 — 미리 등록한 학원에 실제 원장(uid)을 붙인다.
   * (카카오 로그인 후 생긴 계정 uid 사용)
   */
  linkOwner: async (aid, uid) => {
    await updateDoc(academyDoc(aid), { ownerId: uid, unclaimed: false });
    await setDoc(memberDoc(aid, uid), {
      role: 'owner', status: 'approved', joinedAt: serverTimestamp(),
    }, { merge: true });
    await setDoc(userDoc(uid), {
      accountType: 'academy',
      memberships: arrayUnion({ academyId: aid, role: 'owner' }),
    }, { merge: true });
  },

  /** 승인된 학원 목록 (디렉토리·학원 찾기용) — 가상 체험 사본(`demoSessionId` 보유) 제외 */
  getApprovedAcademies: async () => {
    const q = query(academiesCol(), where('status', '==', 'approved'));
    const snap = await getDocs(q);
    return snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((a) => !a.demoSessionId);
  },

  /** 학원 구성원 목록 */
  getMembers: async (aid) => {
    const snap = await getDocs(membersCol(aid));
    return snap.docs.map((d) => ({ uid: d.id, ...d.data() }));
  },

  /** 학부모 가입 신청 */
  requestJoin: async (aid, uid, data = {}) => {
    const id = `req-${Date.now()}`;
    await setDoc(joinRequestDoc(aid, id), {
      uid, status: 'pending', ...data, createdAt: serverTimestamp(),
    });
    return id;
  },

  /** 학원으로 들어온 가입 신청 목록 (원장용) */
  getJoinRequests: async (aid) => {
    const snap = await getDocs(joinRequestsCol(aid));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  },

  /** 가입 신청 승인 — 학부모를 구성원으로 등록 */
  approveJoin: async (aid, requestId, uid) => {
    await setDoc(memberDoc(aid, uid), {
      role: 'parent', status: 'approved', joinedAt: serverTimestamp(),
    });
    await setDoc(userDoc(uid), {
      memberships: arrayUnion({ academyId: aid, role: 'parent' }),
    }, { merge: true });
    await updateDoc(joinRequestDoc(aid, requestId), { status: 'approved' });
  },

  /** 학원 가입 — 선택한 자녀를 학원 학생 명단에 자동 등록 */
  joinAcademy: async (aid, uid, children = []) => {
    const childList = children.map((c) => ({ id: c.id, name: c.name }));
    await setDoc(memberDoc(aid, uid), {
      role: 'parent', status: 'approved', children: childList, joinedAt: serverTimestamp(),
    });
    await setDoc(userDoc(uid), {
      memberships: arrayUnion({
        academyId: aid, role: 'parent', children: childList,
        joinedAt: new Date().toISOString(),
      }),
    }, { merge: true });
    // 선택한 자녀를 학원 학생으로 자동 등록 (자녀 id = 학생 문서 id)
    for (const c of childList) {
      await setDoc(studentDoc(aid, c.id), {
        name: c.name, childId: c.id, parentUid: uid, createdAt: serverTimestamp(),
      });
    }
  },

  /* ── 학생·출석 (MVP 운영) ──────────────────────── */

  /** 학생 추가 — addStudent(aid, '이름') 또는 addStudent(aid, '이름', '01012345678') 또는 addStudent(aid, {name, phone}) */
  addStudent: async (aid, nameOrData, phoneArg) => {
    const data = typeof nameOrData === 'string'
      ? { name: nameOrData, phone: phoneArg || '' }
      : { ...nameOrData };
    if (data.phone) data.phoneE164 = toE164KR(data.phone);
    const id = `stu-${Date.now()}`;
    await setDoc(studentDoc(aid, id), { ...data, createdAt: serverTimestamp() });
    return { id, ...data };
  },

  /** 학생 정보 수정 (이름·전화번호 등) */
  updateStudent: async (aid, sid, data) => {
    const patch = { ...data };
    if (patch.phone !== undefined) patch.phoneE164 = toE164KR(patch.phone);
    await setDoc(studentDoc(aid, sid), patch, { merge: true });
  },

  /** 학생 삭제 */
  deleteStudent: async (aid, sid) => {
    await deleteDoc(studentDoc(aid, sid));
  },

  /** 전화번호 뒷자리 4자리로 학생 찾기 (키오스크용) */
  findStudentsByPhoneLast4: async (aid, last4) => {
    const all = await academyService.getStudents(aid);
    return all.filter((s) => {
      if (!s.phone) return false;
      const digits = String(s.phone).replace(/[^0-9]/g, '');
      return digits.length >= 4 && digits.slice(-4) === last4;
    });
  },

  /** 키오스크 출석 — 학생이 속한 클래스 전부에 등원/퇴실 시각 기록 */
  kioskMarkAttendance: async (aid, sid, kind) => {
    const pad = (n) => String(n).padStart(2, '0');
    const now = new Date();
    const date = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
    const hm = `${pad(now.getHours())}:${pad(now.getMinutes())}`;
    const classes = await academyService.getClasses(aid);
    const mine = classes.filter((c) => (c.studentIds || []).includes(sid));
    for (const c of mine) {
      // eslint-disable-next-line no-await-in-loop
      const cAtt = await academyService.getClassAttendance(aid, c.id, date);
      const rec = cAtt.records || {};
      const cur0 = rec[sid];
      const cur = typeof cur0 === 'string' ? { status: cur0 } : (cur0 || {});
      const updated = { ...cur, status: 'present' };
      if (kind === 'in' && !cur.inAt) updated.inAt = hm;
      if (kind === 'out') updated.outAt = hm;
      const next = { ...rec, [sid]: updated };
      // eslint-disable-next-line no-await-in-loop
      await academyService.saveClassAttendance(aid, c.id, date, next);
    }
    return { time: hm, classes: mine.length };
  },

  /** 학생 명단 — 가나다순 */
  getStudents: async (aid) => {
    const snap = await getDocs(studentsCol(aid));
    return snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'ko'));
  },

  /** 특정 날짜 출석 저장 — records = { [studentId]: 'present'|'absent' } */
  saveAttendance: async (aid, date, records) => {
    await setDoc(attendanceLogDoc(aid, date), { date, records, updatedAt: serverTimestamp() });
  },

  /** 특정 날짜 출석 조회 */
  getAttendance: async (aid, date) => {
    const snap = await getDoc(attendanceLogDoc(aid, date));
    return snap.exists() ? snap.data() : { date, records: {} };
  },

  /** 전체 출석 기록 (최신순) */
  getAttendanceLog: async (aid) => {
    const snap = await getDocs(attendanceLogCol(aid));
    return snap.docs
      .map((d) => d.data())
      .sort((a, b) => (b.date || '').localeCompare(a.date || ''));
  },

  /* ── 채팅 (학부모 ↔ 학원) ─────────────────────── */

  /** 채팅 메시지 전송 — roomId는 학부모 uid (학원별 1:1 방) */
  sendChatMessage: async (aid, roomId, {
    senderId, senderName = '', text = '', type = 'text', fileUrl = '',
  }) => {
    // roomId = 학부모 uid. 보낸 사람이 학부모면 학원 안읽음+1, 아니면 학부모 안읽음+1
    const fromParent = senderId === roomId;
    const preview = type === 'image' ? '사진' : text;
    await setDoc(chatRoomDoc(aid, roomId), {
      parentUid: roomId,
      lastMessage: preview,
      updatedAt: serverTimestamp(),
      [fromParent ? 'academyUnread' : 'parentUnread']: increment(1),
    }, { merge: true });
    await addDoc(messagesCol(aid, roomId), {
      senderId, senderName, text, type, fileUrl, createdAt: serverTimestamp(),
    });
    // 학부모 채팅 목록용 — 사용자 문서에 채팅한 학원 id 기록
    await setDoc(userDoc(roomId), { chatAcademyIds: arrayUnion(aid) }, { merge: true });
  },

  /** 채팅 이미지 업로드 → 다운로드 URL */
  uploadChatImage: async (aid, roomId, blob) => {
    const r = storageRef(storage, `chats/${aid}/${roomId}/${Date.now()}.jpg`);
    await uploadBytes(r, blob);
    return getDownloadURL(r);
  },

  /** 채팅방 단건 조회 */
  getChatRoom: async (aid, roomId) => {
    const snap = await getDoc(chatRoomDoc(aid, roomId));
    return snap.exists() ? snap.data() : null;
  },

  /** 채팅방 읽음 처리 — who: 'parent' | 'academy' */
  markChatRead: async (aid, roomId, who) => {
    await setDoc(chatRoomDoc(aid, roomId), {
      [who === 'academy' ? 'academyUnread' : 'parentUnread']: 0,
    }, { merge: true });
  },

  /** 채팅방 나가기 — 채팅 목록에서 제거 */
  leaveChatRoom: async (aid, uid) => {
    await setDoc(userDoc(uid), { chatAcademyIds: arrayRemove(aid) }, { merge: true });
  },

  /** 채팅 메시지 실시간 구독 */
  watchChatMessages: (aid, roomId, cb) => {
    const q = query(messagesCol(aid, roomId), orderBy('createdAt', 'asc'));
    return onSnapshot(q, (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
  },

  /* ── 클래스 (수업 묶음 + 학생 배정) ─────────────── */

  /** 클래스 목록 */
  getClasses: async (aid) => {
    const snap = await getDocs(classesCol(aid));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  },

  /** 클래스 단건 조회 */
  getClass: async (aid, cid) => {
    const snap = await getDoc(classDoc(aid, cid));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  },

  /** 클래스 생성 */
  createClass: async (aid, data) => {
    const id = `class-${Date.now()}`;
    await setDoc(classDoc(aid, id), {
      name: data.name || '',
      subject: data.subject || '',
      teacherName: data.teacherName || '',
      room: data.room || '',
      color: data.color || '#4F46E5',
      schedule: data.schedule || [],
      supplements: [],
      studentIds: [],
      students: 0,
      createdAt: serverTimestamp(),
    });
    return id;
  },

  /** 클래스 수정 */
  updateClass: async (aid, cid, data) => {
    await updateDoc(classDoc(aid, cid), data);
  },

  /** 클래스 삭제 */
  deleteClass: async (aid, cid) => {
    await deleteDoc(classDoc(aid, cid));
  },

  /* ── 클래스 출석 ───────────────────────────────── */

  /** 특정 날짜 출석 조회 — records = { [studentId]: 'present'|'absent' } */
  getClassAttendance: async (aid, cid, date) => {
    const snap = await getDoc(attendanceDoc(aid, cid, date));
    return snap.exists() ? snap.data() : { date, records: {} };
  },

  /** 특정 날짜 출석 저장 */
  saveClassAttendance: async (aid, cid, date, records) => {
    await setDoc(attendanceDoc(aid, cid, date), { date, records, updatedAt: serverTimestamp() });
  },

  /** 클래스 출석 전체 기록 (최신순) */
  getClassAttendanceLog: async (aid, cid) => {
    const snap = await getDocs(attendanceCol(aid, cid));
    return snap.docs
      .map((d) => d.data())
      .sort((a, b) => (b.date || '').localeCompare(a.date || ''));
  },

  /* ── 클래스 공지 ───────────────────────────────── */

  getNotices: async (aid, cid) => {
    const snap = await getDocs(noticesCol(aid, cid));
    return snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
  },

  addNotice: async (aid, cid, { title, content }) => {
    await addDoc(noticesCol(aid, cid), { title, content, createdAt: serverTimestamp() });
  },

  deleteNotice: async (aid, cid, nid) => {
    await deleteDoc(noticeDoc(aid, cid, nid));
  },

  /* ── 클래스 과제 ───────────────────────────────── */

  /** 과제 목록 (최신순) */
  getAssignments: async (aid, cid) => {
    const snap = await getDocs(assignmentsCol(aid, cid));
    return snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
  },

  /** 과제 추가 — data = { title, description, dueDate } */
  addAssignment: async (aid, cid, data) => {
    const ref = await addDoc(assignmentsCol(aid, cid), {
      ...data,
      submissions: {},
      createdAt: serverTimestamp(),
    });
    return ref.id;
  },

  /** 과제 수정 (submissions 통째 포함) */
  updateAssignment: async (aid, cid, asgId, data) => {
    await updateDoc(assignmentDoc(aid, cid, asgId), data);
  },

  /** 학생 과제 제출 — { text, fileUrl, fileName } */
  submitAssignment: async (aid, cid, asgId, studentId, data = {}) => {
    const snap = await getDoc(assignmentDoc(aid, cid, asgId));
    const cur = snap.exists() ? (snap.data().submissions || {}) : {};
    const now = new Date().toISOString();
    const next = {
      ...cur,
      [studentId]: { ...(cur[studentId] || {}), submitted: true, submittedAt: now, ...data },
    };
    await updateDoc(assignmentDoc(aid, cid, asgId), { submissions: next });
  },

  /** 과제 채점 — scores: { [studentId]: { score, feedback } } */
  gradeAssignment: async (aid, cid, asgId, scores) => {
    const snap = await getDoc(assignmentDoc(aid, cid, asgId));
    const cur = snap.exists() ? (snap.data().submissions || {}) : {};
    const next = { ...cur };
    Object.entries(scores).forEach(([sid, v]) => {
      next[sid] = { ...(next[sid] || {}), ...v };
    });
    await updateDoc(assignmentDoc(aid, cid, asgId), { submissions: next });
  },

  /** 과제 삭제 */
  deleteAssignment: async (aid, cid, asgId) => {
    await deleteDoc(assignmentDoc(aid, cid, asgId));
  },

  /* ── 클래스 성적 (쪽지·중간·기말 등) ─────────────── */

  /**
   * 성적 목록 (최신순)
   * 각 문서 = 시험 한 번 (제목 + 학생별 점수 맵)
   */
  getGrades: async (aid, cid) => {
    const snap = await getDocs(gradesCol(aid, cid));
    return snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
  },

  /**
   * 성적 등록 — data = { title, max, scores: { [studentId]: number } }
   * 원장이 한 시험에 대해 학생별 점수를 한꺼번에 입력
   */
  addGrade: async (aid, cid, data) => {
    const ref = await addDoc(gradesCol(aid, cid), {
      title: data.title || '',
      max: typeof data.max === 'number' ? data.max : 100,
      scores: data.scores || {},
      createdAt: serverTimestamp(),
    });
    return ref.id;
  },

  /** 성적 수정 (제목·만점·점수맵 부분 업데이트) */
  updateGrade: async (aid, cid, gid, data) => {
    await updateDoc(gradeDoc(aid, cid, gid), data);
  },

  /** 성적 삭제 */
  deleteGrade: async (aid, cid, gid) => {
    await deleteDoc(gradeDoc(aid, cid, gid));
  },

  /* ── 클래스 수업일지 ───────────────────────────── */

  /** 수업일지 목록 (날짜 desc) */
  getLogs: async (aid, cid) => {
    const snap = await getDocs(logsCol(aid, cid));
    return snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (b.date || '').localeCompare(a.date || ''));
  },

  /** 수업일지 추가 — data = { date('YYYY-MM-DD'), content } */
  addLog: async (aid, cid, data) => {
    const ref = await addDoc(logsCol(aid, cid), {
      ...data,
      createdAt: serverTimestamp(),
    });
    return ref.id;
  },

  /** 수업일지 삭제 */
  deleteLog: async (aid, cid, lid) => {
    await deleteDoc(logDoc(aid, cid, lid));
  },

  /* ── 클래스 자료실 ─────────────────────────────── */

  getMaterials: async (aid, cid) => {
    const snap = await getDocs(materialsCol(aid, cid));
    return snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
  },

  addMaterial: async (aid, cid, data) => {
    await addDoc(materialsCol(aid, cid), { ...data, createdAt: serverTimestamp() });
  },

  deleteMaterial: async (aid, cid, mid) => {
    await deleteDoc(materialDoc(aid, cid, mid));
  },

  /* ── 학원 일정 ─────────────────────────────────── */

  /** 학원 일정 목록 (날짜·시간 오름차순) */
  getEvents: async (aid) => {
    const snap = await getDocs(academyEventsCol(aid));
    return snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .sort((a, b) =>
        (a.date || '').localeCompare(b.date || '')
        || (a.time || '').localeCompare(b.time || ''));
  },

  /** 일정 추가 — data = { title, date('YYYY-MM-DD'), time, memo } */
  addEvent: async (aid, data) => {
    const ref = await addDoc(academyEventsCol(aid), { ...data, createdAt: serverTimestamp() });
    return ref.id;
  },

  /** 일정 삭제 */
  deleteEvent: async (aid, id) => {
    await deleteDoc(academyEventDoc(aid, id));
  },

  /* ── 학원 공지 (학원 전체 대상) ─────────────────── */

  /** 학원 공지 목록 (최신순) */
  getAcademyNotices: async (aid) => {
    const snap = await getDocs(academyNoticesCol(aid));
    return snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
  },

  /** 학원 공지 등록 */
  addAcademyNotice: async (aid, { title, content }) => {
    const ref = await addDoc(academyNoticesCol(aid), {
      title, content, createdAt: serverTimestamp(),
    });
    return ref.id;
  },

  /** 학원 공지 삭제 */
  deleteAcademyNotice: async (aid, id) => {
    await deleteDoc(academyNoticeDoc(aid, id));
  },

  /* ── 학원 자료실 (학원 전체 공유) ───────────────── */

  /** 학원 자료 목록 (최신순) */
  getAcademyMaterials: async (aid) => {
    const snap = await getDocs(academyMaterialsCol(aid));
    return snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
  },

  /** 학원 자료 등록 — data = { title, fileUrl, fileName } */
  addAcademyMaterial: async (aid, data) => {
    const ref = await addDoc(academyMaterialsCol(aid), {
      ...data, createdAt: serverTimestamp(),
    });
    return ref.id;
  },

  /** 학원 자료 삭제 */
  deleteAcademyMaterial: async (aid, id) => {
    await deleteDoc(academyMaterialDoc(aid, id));
  },
};

export default academyService;
